import React, { useMemo } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Paper, Typography } from "@mui/material";
import { QuestionResponsesSearchResponse } from "@/models/Filter";
import { NoDataIcon } from "@/assets/images";
import EmptyChartState from "@components/common/EmptyChartState";
import {
  getTimePeriodFooterText,
  getTimePeriodNominalRepresentation,
} from "@/utils/time-period-display";
import {
  getRollingAverageFooterText,
  getRollingAverageComparisonFooterText,
  getMissingDataWarning,
  getPeriodTypeLabel,
} from "@/utils/rolling-average-utils";
import {
  resolveTimePeriodInWindow,
  getTimePeriodDates,
} from "@/utils/time-period-utils";
import type { TimePeriod } from "@/store/slices/table-config-slice";

const NAMED_PERIOD_VALUES = new Set([
  "latest_quarter",
  "current_quarter",
  "previous_quarter",
  "last_quarter",
  "latest_semester",
  "current_semester",
  "previous_semester",
  "last_semester",
]);

interface TableChartProps {
  config?: any;
  filteredData?: QuestionResponsesSearchResponse | null;
  hasCustomTitle?: boolean | null; // Whether the user has customized the title
  weightsApplied?: boolean; // Whether weights were actually applied by the API
  hasActiveFilters?: boolean;
  dashboardDateOverride?: { startDate: string | null; endDate: string | null };
  viewerState?: {
    rollingAverage: number | null;
    timeUnit: string | null;
    timePeriod: string | null;
  };
  dataAvailabilityDate?: string | null;
}

const TableChart: React.FC<TableChartProps> = ({
  config,
  filteredData,
  hasCustomTitle,
  weightsApplied = true,
  hasActiveFilters = false,
  dashboardDateOverride,
  viewerState,
  dataAvailabilityDate,
}) => {
  // Use filtered data if available, otherwise use config data
  const data = filteredData?.data || config?.data || [];
  const dataSorting = config?.dataSorting || "none";
  const rounding = config?.percentageFormatting ?? 0;
  const rowHeaderLabel = config?.rowHeaderLabel || "RQ DIMENSIONS";
  const columnHeaderLabel = config?.columnHeaderLabel || "MEAN";
  const hiddenRows = config?.hiddenRows || [];
  const rowOrder = config?.rowOrder || [];
  const customRowLabels = config?.customRowLabels || {};
  // Default statistical testing config if not present (for backwards compatibility)
  const statisticalTesting = config?.statisticalTesting ?? {
    enabled: false,
    testType: "z-test",
    significanceLevel: "90%",
    minimumSampleSize: 30,
    compareToTotal: false,
  };
  const statisticalTestingEnabled = statisticalTesting.enabled;

  // Get weight configuration
  const weight = config?.weight || "DISABLED";

  // Calculate weight footer text
  const weightFooterText = useMemo(() => {
    if (weight === "DISABLED") {
      return "Weights: Disabled (unweighted counts shown)";
    } else if (weight === "DATASET_DEFAULT") {
      if (weightsApplied) {
        return "Weights: Dataset Default (applied)";
      } else {
        return "Weights: Dataset Default (no default weight configured, showing unweighted counts)";
      }
    }
    return null;
  }, [weight, weightsApplied]);

  // Calculate time period footer text if needed
  const timePeriodFooterText = useMemo(() => {
    const viewerTP = viewerState?.timePeriod ?? null;
    const savedTP = config?.timePeriodSelection?.timePeriod;
    const effectivePeriod = viewerTP ?? savedTP;

    if (!effectivePeriod || effectivePeriod === "all_time") return null;
    if (!hasCustomTitle) return null;

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getTimePeriodFooterText(
        viewerTP as TimePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getTimePeriodFooterText(
      effectivePeriod as TimePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.timePeriod,
    config?.timePeriodSelection,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  // Calculate dashboard filter note for Module 4 contextual annotations
  const dashboardFilterNote = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate)
      return null;
    // Viewer's selected period takes precedence over the saved config period.
    const effectiveTimePeriod = (viewerState?.timePeriod ??
      config?.timePeriodSelection?.timePeriod) as TimePeriod | undefined;
    if (!effectiveTimePeriod || !NAMED_PERIOD_VALUES.has(effectiveTimePeriod)) {
      return `Time Period (filtered): ${dashboardDateOverride.startDate} – ${dashboardDateOverride.endDate}`;
    }
    const effectiveEnd =
      dataAvailabilityDate &&
      dataAvailabilityDate < dashboardDateOverride.endDate
        ? dataAvailabilityDate
        : dashboardDateOverride.endDate;
    const resolved = resolveTimePeriodInWindow(
      effectiveTimePeriod,
      dashboardDateOverride.startDate,
      effectiveEnd,
    );
    if (!resolved) {
      const unit = effectiveTimePeriod.includes("semester")
        ? "semester"
        : "quarter";
      return `No complete ${unit} available within the selected date range.`;
    }
    const label = getTimePeriodNominalRepresentation(
      effectiveTimePeriod,
      resolved.startDate,
      resolved.endDate,
    );

    // If rolling average is enabled, show rolling average footer format.
    // Respect viewer's RA override: explicit 0 disables; positive replaces saved.
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const savedRAEnabled =
      !!config?.rollingAverage?.enabled && !!config?.rollingAverage?.windowSize;
    const effectiveRAEnabled =
      !viewerExplicitlyDisabledRA && (hasViewerRA || savedRAEnabled);

    if (effectiveRAEnabled) {
      // Per PRD-35: the dashboard date filter does not restrict the rolling
      // average window size. Lookback may extend outside the filter range.
      // Use configured windowSize (or viewer's RA), NOT effectiveRollingPeriodCount.
      const windowSize = hasViewerRA
        ? (viewerRA as number)
        : (config!.rollingAverage!.windowSize as number);
      const rollingText = getRollingAverageFooterText(
        windowSize,
        effectiveTimePeriod,
        resolved.startDate,
        resolved.endDate,
      );
      // Replace "Rolling Average:" with "Rolling Average (filtered):"
      return rollingText.replace(
        "Rolling Average:",
        "Rolling Average (filtered):",
      );
    }

    // If stat testing is enabled, show "X (filtered): A vs B" for all named period
    // types. The comparison period is always the complete period immediately before
    // the displayed period — per PRD-35 it is allowed to be outside the filter range.
    if (statisticalTestingEnabled) {
      const periodLabels: Partial<Record<TimePeriod, string>> = {
        latest_quarter: "Latest Quarter",
        previous_quarter: "Previous Quarter",
        latest_semester: "Latest Semester",
        previous_semester: "Previous Semester",
      };
      const periodLabel = periodLabels[effectiveTimePeriod];

      if (periodLabel) {
        const dayBefore = new Date(resolved.startDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const dayBeforeStr = dayBefore.toISOString().split("T")[0];

        const comparisonType = (
          effectiveTimePeriod.includes("semester")
            ? "latest_semester"
            : "latest_quarter"
        ) as TimePeriod;
        const comparisonResolved = resolveTimePeriodInWindow(
          comparisonType,
          "2000-01-01",
          dayBeforeStr,
        );

        if (comparisonResolved) {
          const comparisonLabel = getTimePeriodNominalRepresentation(
            comparisonType,
            comparisonResolved.startDate,
            comparisonResolved.endDate,
          );
          if (comparisonLabel) {
            return `${periodLabel} (filtered): ${label} vs ${comparisonLabel}`;
          }
        }
      }
    }

    return label ? `Time Period (filtered): ${label}` : null;
  }, [
    dashboardDateOverride,
    config?.timePeriodSelection?.timePeriod,
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    statisticalTestingEnabled,
    viewerState?.timePeriod,
    viewerState?.rollingAverage,
    dataAvailabilityDate,
  ]);

  // Suppress stat testing when the dashboard date filter yields no complete period
  const isStatTestingSuppressed =
    !!dashboardFilterNote?.startsWith("No complete");

  // Calculate rolling average footer text if needed (only when title is customized)
  const rollingAverageFooterText = useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const savedRAEnabled =
      config?.rollingAverage?.enabled && config.rollingAverage.windowSize;

    if (viewerExplicitlyDisabledRA) return null;

    if (!hasViewerRA && (!savedRAEnabled || !hasCustomTitle)) {
      return null;
    }

    const savedTP = config?.timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? viewerRA
      : (config.effectiveRollingPeriodCount ??
        config.rollingAverage.windowSize);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageFooterText(
      effectiveWindowSize,
      timePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    config?.timePeriodSelection?.timePeriod,
    config?.timePeriodSelection?.startDate,
    config?.timePeriodSelection?.endDate,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  // Calculate rolling average comparison footer text if needed
  const rollingAverageComparisonFooterText = useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;

    if (viewerExplicitlyDisabledRA) return null;

    const savedRAEnabled =
      !!config?.rollingAverage?.enabled && !!config?.rollingAverage?.windowSize;

    if (!hasViewerRA && !savedRAEnabled) return null;

    const savedTP = config?.timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? (viewerRA as number)
      : (config!.rollingAverage!.windowSize as number);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageComparisonFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageComparisonFooterText(
      effectiveWindowSize,
      timePeriod,
      config?.timePeriodSelection?.startDate,
      config?.timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    config?.rollingAverage,
    config?.timePeriodSelection?.timePeriod,
    config?.timePeriodSelection?.startDate,
    config?.timePeriodSelection?.endDate,
    dataAvailabilityDate,
  ]);

  // Calculate missing data warning when rolling window extends before dataset start
  const missingDataWarning = useMemo(() => {
    if (!config?.rollingAverage?.enabled || !config.rollingAverage.windowSize) {
      return null;
    }

    const timePeriod = config?.timePeriodSelection?.timePeriod;
    if (!timePeriod) return null;

    return getMissingDataWarning(
      config.rollingAverage.windowSize,
      config?.effectiveRollingPeriodCount,
      config?.missingRollingPeriodLabels,
    );
  }, [
    config?.rollingAverage,
    config?.effectiveRollingPeriodCount,
    config?.missingRollingPeriodLabels,
  ]);

  // Sort and filter data based on configuration
  const sortedData = useMemo(() => {
    // Filter out hidden rows
    const visibleData = data.filter((item: any) => {
      return !hiddenRows.includes(item.variableId);
    });

    // If rowOrder exists and has items, use it for ordering (for "none" or "manual" sorting)
    if (
      rowOrder.length > 0 &&
      (dataSorting === "none" || dataSorting === "manual")
    ) {
      // Create a map for quick lookup
      const dataMap = new Map(
        visibleData.map((item: any) => [item.variableId, item]),
      );

      // Order data according to rowOrder, filtering out any that don't exist
      const orderedData = rowOrder
        .map((variableId: string) => dataMap.get(variableId))
        .filter((item: any) => item !== undefined);

      return orderedData;
    }

    if (dataSorting === "none") {
      return visibleData;
    }

    const sorted = [...visibleData].sort((a: any, b: any) => {
      // Extract mean value from descriptiveMetrics array
      const aMeanMetric = a.surveys?.[0]?.descriptiveMetrics?.find(
        (m: any) => m.valueMetric === "mean",
      );
      const bMeanMetric = b.surveys?.[0]?.descriptiveMetrics?.find(
        (m: any) => m.valueMetric === "mean",
      );

      const aMean = aMeanMetric?.value ?? 0;
      const bMean = bMeanMetric?.value ?? 0;

      if (dataSorting === "ascending") {
        return aMean - bMean;
      } else {
        return bMean - aMean;
      }
    });

    return sorted;
  }, [data, dataSorting, hiddenRows, rowOrder]);

  // Check with team ! - on baseN extraction approach
  const firstMetric = sortedData[0]?.surveys?.[0]?.descriptiveMetrics?.find(
    (m: any) => m.valueMetric === "mean",
  );
  const baseN = firstMetric?.baseN ?? 0;

  // Transform data into MUI DataGrid format
  const rows = useMemo(() => {
    return sortedData.map((item: any, index: number) => {
      // Extract mean value from descriptiveMetrics array
      const meanMetric = item.surveys?.[0]?.descriptiveMetrics?.find(
        (m: any) => m.valueMetric === "mean",
      );
      const meanValue = meanMetric?.value ?? 0;
      const statSignificance = meanMetric?.statSignificance ?? 0;

      // Use custom label if available, otherwise use original label
      const displayLabel =
        customRowLabels[item.variableId] || item.variableLabel || "-";

      return {
        id: index,
        variable: displayLabel,
        mean: meanValue.toFixed(rounding),
        statSignificance,
      };
    });
  }, [sortedData, rounding, customRowLabels]);

  // Define columns for MUI DataGrid with customizable headers
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "variable",
        headerName: rowHeaderLabel,
        flex: 1,
        sortable: false,
        headerClassName: "table-header",
        cellClassName: "table-cell",
      },
      {
        field: "mean",
        headerName: columnHeaderLabel,
        flex: 1,
        sortable: false,
        headerAlign: "right",
        align: "right",
        headerClassName: "table-header",
        cellClassName: "table-cell-mean",
        renderCell: (params: GridRenderCellParams) => {
          const statSignificance = params.row.statSignificance;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                width: "100%",
              }}
            >
              {statisticalTestingEnabled && (
                <div style={{ width: "16px", textAlign: "center" }}>
                  {statSignificance === 1 && (
                    <span className="text-[#10B981]">▲</span>
                  )}
                  {statSignificance === -1 && (
                    <span className="text-[#EF4444]">▼</span>
                  )}
                </div>
              )}
              <p className="w-[40px]">{params.value}</p>
            </Box>
          );
        },
      },
    ],
    [rowHeaderLabel, columnHeaderLabel, statisticalTestingEnabled],
  );
  if (isStatTestingSuppressed && dashboardFilterNote) {
    return (
      <div className="w-full h-full bg-white flex flex-col">
        <EmptyChartState message={dashboardFilterNote} />
      </div>
    );
  }

  // Showing meaningful message instead of no rows
  if (rows.length === 0) {
    return (
      <div className="w-full h-full bg-white flex flex-col items-center justify-center">
        <NoDataIcon
          style={{
            width: "110px",
            height: "110px",
            color: "var(--color-hp-bg-disabled)",
          }}
        />
        <p className="text-sm font-medium">
          {hasActiveFilters
            ? "No data for current filter combination."
            : "No data available."}
        </p>
      </div>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          columnHeaderHeight={56}
          rowHeight={52}
          sx={{
            height: "100%",
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeader": {
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            },
          }}
        />
      </Box>
      <Box
        sx={{
          mt: 1,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {dashboardFilterNote ? (
            <Typography variant="caption" color="primary" fontWeight={500}>
              {dashboardFilterNote}
            </Typography>
          ) : (
            timePeriodFooterText && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                {timePeriodFooterText}
              </Typography>
            )
          )}
          {rollingAverageFooterText && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {rollingAverageFooterText}
            </Typography>
          )}
          {rollingAverageComparisonFooterText && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {rollingAverageComparisonFooterText}
            </Typography>
          )}
          {config?.rollingAverage?.enabled &&
            config?.comparisonRollingLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                {config.comparisonRollingLabel}
              </Typography>
            )}
          {config?.rollingPeriodsWarningMessage && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {config.rollingPeriodsWarningMessage}
            </Typography>
          )}
          {missingDataWarning && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {missingDataWarning}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Base N: {baseN.toLocaleString()}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight:{" "}
          {weight === "DISABLED"
            ? "No Weight"
            : weightsApplied
              ? "Default Weight"
              : "No Weight"}
        </Typography>
      </Box>
      {statisticalTestingEnabled &&
        !isStatTestingSuppressed &&
        (() => {
          const hasSignificantChanges = rows.some(
            (row: any) => row.statSignificance !== 0,
          );
          const significanceLevel =
            statisticalTesting.significanceLevel || "90%";
          const isRollingAverage =
            config?.rollingAverage?.enabled &&
            config.rollingAverage.windowSize !== undefined &&
            config.rollingAverage.windowSize > 0;
          // Use effectiveRollingPeriodCount (N = actual periods with data) if available,
          // otherwise fall back to configured windowSize
          const windowSize =
            config?.effectiveRollingPeriodCount ??
            config?.rollingAverage?.windowSize;
          const periodType = config?.timePeriodSelection?.timePeriod
            ? getPeriodTypeLabel(config.timePeriodSelection.timePeriod)
            : "period";
          const comparisonLabel = isRollingAverage
            ? `Previous Rolling Window`
            : `Previous Period`;
          const comparisonDescription = isRollingAverage
            ? `previous overlapping ${windowSize}-${periodType} rolling average`
            : `previous period`;

          if (hasSignificantChanges) {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  <span className="text-[#10B981]">▲</span> /{" "}
                  <span className="text-[#EF4444]">▼</span> {comparisonLabel}:
                  Significant increase/decrease compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No statistically significant differences found compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          }
        })()}
    </Paper>
  );
};

export default TableChart;
